import { Link } from "react-router-dom";
import type { DirectoryPerson } from "@sales-ops-amplify/shared";
import {
  personCompanyLinkPath,
  personOrganizationName,
} from "@/lib/person-company-link";

export function PersonDrawer({
  person,
  onClose,
  onSave,
  saving,
}: {
  person: DirectoryPerson;
  onClose: () => void;
  onSave: () => void;
  /** @deprecated Apollo unlock — unused for directory data */
  onEnrich?: () => void;
  saving?: boolean;
  enriching?: boolean;
}) {
  const companyPath = personCompanyLinkPath(person, "/companies");
  const orgName = personOrganizationName(person);
  const org = person.organization;
  const location = [person.city, person.state, person.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onClose}>
      <aside
        className="flex h-full w-full max-w-md flex-col border-l border-border bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{person.name}</h2>
            <p className="text-sm text-muted-foreground">
              {person.title ?? "—"}
              {orgName !== "—" ? ` · ${orgName}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
          <section className="space-y-1">
            <p className="text-xs font-semibold uppercase text-muted-foreground">
              Person
            </p>
            <p>Email: {person.email ?? "—"}</p>
            <p>Phone: {person.phone ?? person.mobilePhone ?? "—"}</p>
            <p>Location: {location || "—"}</p>
            <p>Seniority: {person.seniority ?? "—"}</p>
            <p>Stage: {person.stage ?? "—"}</p>
            <p>Owner: {person.leadOwner || "—"}</p>
            {person.departments?.length ? (
              <p>Departments: {person.departments.join(", ")}</p>
            ) : null}
            {person.subDepartments?.length ? (
              <p>Sub-departments: {person.subDepartments.join(", ")}</p>
            ) : null}
            {person.linkedinUrl && (
              <a
                href={person.linkedinUrl}
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:underline"
              >
                LinkedIn profile
              </a>
            )}
          </section>

          <section className="space-y-1">
            <p className="text-xs font-semibold uppercase text-muted-foreground">
              Company
            </p>
            <p className="font-medium">{orgName}</p>
            <p>Domain: {org?.domain ?? "—"}</p>
            <p>Industry: {org?.industry ?? "—"}</p>
            <p>
              Employees:{" "}
              {org?.employeeCount?.toLocaleString() ?? "—"}
            </p>
            {companyPath ? (
              <Link to={companyPath} className="text-brand hover:underline">
                Open company
              </Link>
            ) : null}
          </section>

          {person.alreadySaved && (
            <p className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
              Already in leads
              {person.leadId ? ` · ${person.leadId}` : ""}.
            </p>
          )}
        </div>

        <footer className="flex flex-wrap gap-2 border-t border-border px-5 py-4">
          <Link
            to={`/people/${person.personId || person.apolloPersonId}`}
            className="rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted/40"
          >
            Open profile
          </Link>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || person.alreadySaved}
            className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-foreground disabled:opacity-50"
          >
            {saving ? "Saving…" : person.alreadySaved ? "In leads" : "Add to leads"}
          </button>
        </footer>
      </aside>
    </div>
  );
}
